import { useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
    selectNoteByType,
    selectCaseNotesStatus,
    selectCaseNotesError,
} from '../features/remarks/logic/CaseNotesSlice'
import { CaseNoteType } from '../api/CaseNote';
import { contentTypeMap } from '../api/Content';
import { UpdateCaseNoteRequest } from '../api/CaseNote';
import {generateContentThunk, improveContentThunk} from '../thunks/Content';
import { updateCaseNote as updateCaseNoteThunk } from '../thunks/CaseNote';



export interface UseCaseNotesProps {
  noteType: CaseNoteType;
}

export interface UseCaseNotesReturn {
  initialValue: string;
  isLoading: boolean;
  error: string | null;
  onSave: (newText: string) => Promise<void>;
  onImprove: (currentText: string) => Promise<string | null>;
  onGenerate: () => Promise<string | undefined>;
}

export const useCaseNotes = ({ noteType }: UseCaseNotesProps): UseCaseNotesReturn => {
  const dispatch = useAppDispatch();
  const { id: caseId } = useParams<{ id: string }>();
  
  const status = useAppSelector((state) => selectCaseNotesStatus(state, caseId));
  const error = useAppSelector((state) => selectCaseNotesError(state, caseId));
  
  const noteData = useAppSelector((state) => {
    // TODO - https://github.com/Yugen-ai/canso-fraud-investigation-ui/issues/89
    return selectNoteByType(state, caseId, noteType);
  });
  const initialValue = useMemo(() => noteData?.value || '', [noteData]);

  // const noteData = useAppSelector((state) => selectNoteByType(state, caseId, noteType));

  const onSave = useCallback(async (newText: string) => {
    if (!caseId) return;

    const request: UpdateCaseNoteRequest = {
      value: newText,
      user_updated: true,
      ai_generated: noteData?.ai_generated ?? false,
    };
    
    try {
      await dispatch(updateCaseNoteThunk({ caseId, type: noteType, request })).unwrap();
    } catch (err) {
      console.error(`Failed to save note for ${noteType}:`, err);
      throw err;
    }
    }, [caseId, noteType, noteData, dispatch]);

  const onGenerate = useCallback(async () => {
    if (!caseId) return;

    try {
      const backendType = contentTypeMap[noteType];

      const response = await dispatch(generateContentThunk({
        caseId,
        contentType: backendType,
      })).unwrap();

      return response.content;
      } catch (err) {
        console.error(`Failed to generate note for ${noteType}:`, err);
        throw err;
    }
}, [caseId, noteType, dispatch]);

  const onImprove = useCallback(async (currentText: string) => {
    try {
      const response = await dispatch(
        improveContentThunk({ content: currentText })
      ).unwrap();

      return response.content;
    } catch (err) {
      console.error(`Failed to improve note for ${noteType}:`, err);
      throw err;
    }
  }, [dispatch, noteType]);

  return {
    initialValue,
    isLoading: status === 'pending',
    error,
    onSave,
    onGenerate,
    onImprove,
  };
};
